import React, { useState, useEffect, ReactNode } from 'react';
import GlobalSearch from './GlobalSearch';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Globe,
  Search,
  Activity,
  List,
  Briefcase,
  Settings,
  Menu,
  Bell,
  User,
  LogIn,
  LogOut,
  ChevronDown,
  ChevronRight,
  LucideIcon,
  Brain,
  Newspaper,
  TrendingUp,
  ClipboardList,
  Lightbulb,
  BookOpen,
  Bookmark,
  PlayCircle,
  BarChart3,
  Upload,
  HelpCircle,
  MessageSquare,
  MessageCircle,
  Library,
  Landmark,
  Target,
  Sparkles,
  BarChart2,
  AlertTriangle,
  Shield,
  Server,
  CalendarDays,
  Plug,
  Users,
  Building,
  Filter,
  Key,
  Palette,
  Rocket,
  Receipt,
  Lock,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { usePermissionContext } from '../contexts/PermissionContext';
import NotificationCenter from './NotificationCenter';
import OnboardingTour from './OnboardingTour';
import { AnnouncementBanner } from './AnnouncementBanner';
import TrialBanner from './subscription/TrialBanner';
import PriceTicker from './PriceTicker';
import { VERSION } from '../version';
import DisclaimerFooter from './DisclaimerFooter';
import MobileTabBar from './MobileTabBar';
import MobileMenuOverlay from './MobileMenuOverlay';
import { useJSEPrefetch } from '../hooks/useJSEPrefetch';
import { PaymentReminderCard } from './PaymentReminderCard';

const GoldMLogo: React.FC<{ size?: number }> = ({ size = 32 }) => {
  const id = `mg${size}`;
  const pad = size * 0.12;
  const inner = size - pad * 2;
  const pts = [
    [0.23, 0.88], [0.23, 0.40], [0.42, 0.65], [0.50, 0.50],
    [0.58, 0.35], [0.77, 0.62], [0.77, 0.88],
  ].map(([x, y]) => `${(x! * inner + pad).toFixed(2)},${(y! * inner + pad).toFixed(2)}`).join(' ');
  const r = (size * 0.12).toFixed(2);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-stroke`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F5D78E" />
          <stop offset="50%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#8B6914" />
        </linearGradient>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1035" />
          <stop offset="100%" stopColor="#0d0820" />
        </linearGradient>
      </defs>
      <rect width={size} height={size} rx={r} fill={`url(#${id}-bg)`} />
      <rect x="0.5" y="0.5" width={size - 1} height={size - 1} rx={r} stroke="#C9A84C" strokeWidth="0.8" opacity="0.4" />
      <polyline points={pts} stroke={`url(#${id}-stroke)`} strokeWidth={size * 0.065} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

interface NavItemProps {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  collapsed?: boolean;
  onClick?: () => void;
  badge?: number;
}

const NavItem: React.FC<NavItemProps> = ({ icon: Icon, label, active, collapsed, onClick, badge }) => (
  <div
    onClick={onClick}
    style={active ? {
      backgroundColor: 'var(--accent-bg)',
      color: 'var(--accent-400)',
      borderLeftColor: 'var(--accent-border)',
    } : undefined}
    className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all rounded-lg mx-2 relative
      ${active ? 'border-l-2' : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'}`}
  >
    <Icon size={20} />
    {!collapsed && <span className="font-medium">{label}</span>}
    {badge !== undefined && badge > 0 && (
      <span
        style={{ backgroundColor: 'var(--accent-500)', color: '#0f172a' }}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold px-1.5 py-0.5 rounded-full"
      >
        {badge}
      </span>
    )}
  </div>
);

interface NavGroupProps {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  collapsed?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}

const NavGroup: React.FC<NavGroupProps> = ({ icon: Icon, label, active, collapsed, expanded, onToggle, children }) => (
  <div>
    <div
      onClick={onToggle}
      style={active ? {
        backgroundColor: 'var(--accent-bg)',
        color: 'var(--accent-400)',
        borderLeftColor: 'var(--accent-border)',
      } : undefined}
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all rounded-lg mx-2
        ${active ? 'border-l-2' : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'}`}
    >
      <Icon size={20} />
      {!collapsed && (
        <>
          <span className="font-medium flex-1">{label}</span>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </>
      )}
    </div>
    {!collapsed && expanded && (
      <div className="ml-6 border-l border-slate-700/50 pl-2 mt-1 space-y-0.5">
        {children}
      </div>
    )}
  </div>
);

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  useJSEPrefetch();

  const { preferences, updateDisplayPreferences } = usePreferences();
  const sidebarCollapsed = preferences.display.sidebarCollapsed;
  const showPriceTicker = preferences.display.showPriceTicker;
  const setSidebarCollapsed = (val: boolean) => updateDisplayPreferences({ sidebarCollapsed: val });

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tradingExpanded, setTradingExpanded] = useState(false);
  const [marketExpanded, setMarketExpanded] = useState(false);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [learnExpanded, setLearnExpanded] = useState(false);
  const [orgExpanded, setOrgExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { isPremium, isPro } = useSubscription();
  const { data: entitlements } = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => api.getEntitlements(),
    staleTime: 30_000,
    enabled: isAuthenticated,
  });
  const bankerLocked = entitlements?.investment_banking_gate === true && !entitlements.investment_banking?.active;
  const { roles: permissionRoles } = usePermissionContext();
  const isOrgMember = !user?.is_admin && permissionRoles.some((r) =>
    ['org_admin', 'lead_analyst', 'analyst', 'viewer'].includes(r)
  );
  const isOrgAdminNav = permissionRoles.includes('org_admin');
  const isBillingNav = permissionRoles.some((r) => ['org_admin', 'finance_manager'].includes(r));
  const isLeadAnalystOrAdmin = permissionRoles.some((r) => ['lead_analyst', 'org_admin'].includes(r));

  // Auto-expand sections based on current page
  useEffect(() => {
    if (location.pathname.startsWith('/trading') && !location.pathname.startsWith('/trading/appointments')) {
      setTradingExpanded(true);
    }
    const marketPaths = ['/markets', '/screener', '/analysis', '/watchlist', '/portfolio', '/alerts', '/ai-history', '/strategy-history'];
    if (marketPaths.some((p) => location.pathname.startsWith(p))) {
      setMarketExpanded(true);
    }
    if (location.pathname.startsWith('/news') || location.pathname.startsWith('/forex-pairs')) {
      setNewsExpanded(true);
    }
    if (location.pathname.startsWith('/admin')) {
      setAdminExpanded(true);
    }
    if (location.pathname.startsWith('/knowledge')) {
      setLearnExpanded(true);
    }
  }, [location.pathname]);

  // Auto-close mobile menu overlay on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Check if onboarding should be shown.
  // Any authenticated session that reaches this point without the flag is an
  // existing user (not a brand-new registration), so mark it complete and skip
  // the tour to prevent it from blocking the UI on fresh browsers/sessions.
  useEffect(() => {
    if (isAuthenticated) {
      const hasCompletedOnboarding = localStorage.getItem('onboarding_completed');
      if (!hasCompletedOnboarding) {
        localStorage.setItem('onboarding_completed', 'true');
      }
    }
  }, [isAuthenticated]);

  const marketSubItems = [
    { label: 'Markets', icon: Globe, path: '/markets' },
    { label: 'Screener', icon: Search, path: '/screener' },
    { label: 'Analysis', icon: Activity, path: '/analysis' },
    { label: 'Watchlist', icon: List, path: '/watchlist' },
    { label: 'Orders', icon: Receipt, path: '/trading/orders' },
    { label: 'IPO Calendar', icon: Rocket, path: '/markets/ipo' },
    { label: 'Portfolio', icon: Briefcase, path: '/portfolio' },
    { label: 'Alerts', icon: Bell, path: '/alerts' },
    { label: 'AI History', icon: Brain, path: '/ai-history' },
    { label: 'Strategy History', icon: BarChart2, path: '/strategy-history' },
  ];

  const isActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <div className="h-screen bg-slate-900 text-white flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarCollapsed ? 'w-16' : 'w-60'
        } bg-slate-800 border-r border-slate-700/50 hidden lg:flex flex-col transition-all duration-300 fixed h-full z-50`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-700/50 flex items-center gap-3">
          <GoldMLogo size={32} />
          {!sidebarCollapsed && (
            <div>
              <h1 className="font-bold text-lg">my investments</h1>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <NavItem
            icon={Home}
            label="Dashboard"
            active={isActive('/')}
            collapsed={sidebarCollapsed}
            onClick={() => navigate('/')}
          />

          <NavItem
            icon={CalendarDays}
            label="Appointments"
            active={location.pathname.startsWith('/trading/appointments')}
            collapsed={sidebarCollapsed}
            onClick={() => navigate('/trading/appointments')}
          />

          {/* Market Analyst — expandable section */}
          <NavGroup
            icon={Globe}
            label="Market Analyst"
            active={marketSubItems.some((s) => location.pathname.startsWith(s.path))}
            collapsed={sidebarCollapsed}
            expanded={marketExpanded}
            onToggle={() => sidebarCollapsed ? navigate('/markets') : setMarketExpanded(!marketExpanded)}
          >
            {marketSubItems.map((sub) => {
              const SubIcon = sub.icon;
              const subActive = sub.path === '/markets'
                ? location.pathname === '/markets'
                : location.pathname.startsWith(sub.path);
              return (
                <div
                  key={sub.path}
                  onClick={() => navigate(sub.path)}
                  style={subActive ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md text-sm transition-colors ${
                    subActive ? '' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                  }`}
                >
                  <SubIcon size={14} />
                  <span className="flex-1">{sub.label}</span>
                  {bankerLocked && ['/markets/ipo', '/trading/banker/settings', '/trading/banker/leads', '/trading/banker/monitor'].includes(sub.path) && (
                    <Lock size={13} aria-label="Investment Banker subscription required" />
                  )}
                </div>
              );
            })}
          </NavGroup>

          {/* Trading Analyst — expandable section */}
          <NavGroup
            icon={TrendingUp}
            label="Trading Analyst"
            active={location.pathname.startsWith('/trading') && !location.pathname.startsWith('/trading/appointments')}
            collapsed={sidebarCollapsed}
            expanded={tradingExpanded}
            onToggle={() => sidebarCollapsed ? navigate('/trading') : setTradingExpanded(!tradingExpanded)}
          >
            {[
              { label: 'Dashboard', icon: Activity, path: '/trading' },
              { label: 'Analyze', icon: Upload, path: '/trading/analyze' },
              { label: 'Trades', icon: ClipboardList, path: '/trading/trades' },
              { label: 'Performance', icon: BarChart3, path: '/trading/performance' },
              { label: 'Insights', icon: Lightbulb, path: '/trading/insights' },
              { label: 'Expert Chat', icon: MessageCircle, path: '/trading/expert' },
              { label: 'Strategies', icon: Library, path: '/trading/strategies' },
              { label: 'Skills', icon: BookOpen, path: '/trading/skills' },
              { label: 'Monitor', icon: Activity, path: '/trading/banker/monitor', adminOnly: true },
              { label: 'Banker', icon: Landmark, path: '/trading/banker/settings' },
              { label: 'Leads', icon: Target, path: '/trading/banker/leads' },
              { label: 'Settings', icon: Settings, path: '/trading/settings' },
            ].filter((sub) => !('adminOnly' in sub && sub.adminOnly && !user?.is_admin)).map((sub) => {
              const SubIcon = sub.icon;
              const subActive = sub.path === '/trading'
                ? location.pathname === '/trading'
                : location.pathname.startsWith(sub.path);
              return (
                <div
                  key={sub.path}
                  onClick={() => navigate(sub.path)}
                  style={subActive ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md text-sm transition-colors ${
                    subActive ? '' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                  }`}
                >
                  <SubIcon size={14} />
                  <span className="flex-1">{sub.label}</span>
                  {bankerLocked && ['/trading/banker/settings', '/trading/banker/leads', '/trading/banker/monitor'].includes(sub.path) && (
                    <Lock size={13} aria-label="Investment Banker subscription required" />
                  )}
                </div>
              );
            })}
          </NavGroup>

          {/* News & Calendar — expandable section */}
          <NavGroup
            icon={Newspaper}
            label="News & Calendar"
            active={isActive('/news') || isActive('/forex-pairs')}
            collapsed={sidebarCollapsed}
            expanded={newsExpanded}
            onToggle={() => sidebarCollapsed ? navigate('/news') : setNewsExpanded(!newsExpanded)}
          >
            {[
              { label: 'Forex Calendar', icon: AlertTriangle, path: '/news' },
              { label: 'Forex Pairs',    icon: BarChart2,     path: '/forex-pairs' },
            ].map((sub) => {
              const SubIcon = sub.icon;
              const subActive = location.pathname === sub.path || location.pathname.startsWith(sub.path + '/');
              return (
                <div
                  key={sub.path}
                  onClick={() => navigate(sub.path)}
                  style={subActive ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md text-sm transition-colors ${
                    subActive ? '' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                  }`}
                >
                  <SubIcon size={14} />
                  <span>{sub.label}</span>
                </div>
              );
            })}
          </NavGroup>

          {/* Learn — expandable section */}
          <NavGroup
            icon={BookOpen}
            label="Learn"
            active={isActive('/knowledge')}
            collapsed={sidebarCollapsed}
            expanded={learnExpanded}
            onToggle={() => sidebarCollapsed ? navigate('/knowledge') : setLearnExpanded(!learnExpanded)}
          >
            {[
              { label: 'Videos', icon: PlayCircle, path: '/knowledge' },
              { label: 'Glossary', icon: Bookmark, path: '/knowledge/glossary' },
            ].map((sub) => {
              const SubIcon = sub.icon;
              const subActive = sub.path === '/knowledge'
                ? location.pathname === '/knowledge' || (location.pathname.startsWith('/knowledge/') && !location.pathname.startsWith('/knowledge/glossary'))
                : location.pathname.startsWith(sub.path);
              return (
                <div
                  key={sub.path}
                  onClick={() => navigate(sub.path)}
                  style={subActive ? { color: 'var(--accent-400)', backgroundColor: 'var(--accent-bg)' } : undefined}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md text-sm transition-colors ${
                    subActive ? '' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                  }`}
                >
                  <SubIcon size={14} />
                  <span>{sub.label}</span>
                </div>
              );
            })}
          </NavGroup>

          {/* Organisation — only for org members */}
          {isOrgMember && (
            <NavGroup
              icon={Building}
              label="Organisation"
              active={location.pathname.startsWith('/org')}
              collapsed={sidebarCollapsed}
              expanded={orgExpanded}
              onToggle={() => sidebarCollapsed ? navigate('/org/members') : setOrgExpanded(!orgExpanded)}
            >
              {isOrgAdminNav && (
                <NavItem
                  icon={Users}
                  label="Members"
                  active={isActive('/org/members')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/members')}
                />
              )}
              {isBillingNav && (
                <NavItem
                  icon={Landmark}
                  label="Billing"
                  active={isActive('/org/billing')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/billing')}
                />
              )}
              {isLeadAnalystOrAdmin && (
                <NavItem
                  icon={Filter}
                  label="Signal Queue"
                  active={isActive('/org/signals/queue')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/signals/queue')}
                />
              )}
              {isOrgAdminNav && (
                <NavItem
                  icon={Key}
                  label="MCP API Keys"
                  active={isActive('/org/settings/mcp-keys')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/settings/mcp-keys')}
                />
              )}
              {isOrgAdminNav && (
                <NavItem
                  icon={Palette}
                  label="Branding"
                  active={isActive('/org/settings/branding')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/settings/branding')}
                />
              )}
              {isOrgAdminNav && (
                <NavItem
                  icon={Receipt}
                  label="FSP Licence"
                  active={location.pathname.startsWith('/org/fsp-profile')}
                  collapsed={sidebarCollapsed}
                  onClick={() => navigate('/org/fsp-profile')}
                />
              )}
            </NavGroup>
          )}

          {/* Admin — only for admins */}
          {user?.is_admin && (
            <NavGroup
              icon={Shield}
              label="Admin"
              active={isActive('/admin')}
              collapsed={sidebarCollapsed}
              expanded={adminExpanded}
              onToggle={() => sidebarCollapsed ? navigate('/admin') : setAdminExpanded(!adminExpanded)}
            >
              <NavItem
                icon={Shield}
                label="Dashboard"
                active={location.pathname === '/admin'}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin')}
              />
              <NavItem
                icon={Building}
                label="Organisations"
                active={isActive('/admin/organisations')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/organisations')}
              />
              <NavItem
                icon={Shield}
                label="Roles"
                active={isActive('/admin/access-control/roles')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/access-control/roles')}
              />
              <NavItem
                icon={Activity}
                label="Background Tasks"
                active={isActive('/admin/background-tasks')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/background-tasks')}
              />
              <NavItem
                icon={Server}
                label="Services"
                active={isActive('/admin/services')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/services')}
              />
              <NavItem
                icon={BarChart2}
                label="Performance"
                active={isActive('/admin/performance')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/performance')}
              />
              <NavItem
                icon={AlertTriangle}
                label="Exceptions"
                active={isActive('/admin/exceptions')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/exceptions')}
              />
              <NavItem
                icon={MessageSquare}
                label="Support Tickets"
                active={isActive('/admin/support')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/support')}
              />
              <NavItem
                icon={Bell}
                label="Announcements"
                active={isActive('/admin/announcements')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/announcements')}
              />
              {/* hotfix-1-117: surface v43-1-4 Content Approvals + pre-existing AdminGlossary
                  which were both orphaned routes (reachable only by URL). */}
              <NavItem
                icon={ClipboardList}
                label="Content Approvals"
                active={isActive('/admin/content-approvals')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/content-approvals')}
              />
              <NavItem
                icon={BookOpen}
                label="Glossary"
                active={isActive('/admin/knowledge/glossary')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/admin/knowledge/glossary')}
              />
              <NavItem
                icon={Brain}
                label="Personas"
                active={isActive('/settings/personas')}
                collapsed={sidebarCollapsed}
                onClick={() => navigate('/settings/personas')}
              />
            </NavGroup>
          )}
        </nav>

        {/* Settings & What's New */}
        <div className="p-2 border-t border-slate-700/50">
          <NavItem icon={BookOpen} label="FAQ" collapsed={sidebarCollapsed} active={isActive('/faq')} onClick={() => navigate('/faq')} />
          <NavItem icon={HelpCircle} label="Support" collapsed={sidebarCollapsed} active={isActive('/support/tickets')} onClick={() => navigate('/support/tickets')} />
          <NavItem icon={Settings} label="Settings" collapsed={sidebarCollapsed} onClick={() => navigate('/settings')} />
          <div className="relative">
            <NavItem icon={Plug} label="API Access" collapsed={sidebarCollapsed} active={isActive('/settings/mcp')} onClick={() => navigate('/settings/mcp')} />
            {!isPro && !sidebarCollapsed && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded px-1 py-0.5 pointer-events-none">
                PRO+
              </span>
            )}
          </div>
          <NavItem icon={Sparkles} label="What's New" collapsed={sidebarCollapsed} onClick={() => navigate('/releases')} />
        </div>

        {/* Version */}
        {!sidebarCollapsed && (
          <div className="px-4 py-2 text-center border-t border-slate-700/50">
            <p className="text-xs text-slate-500">v{VERSION}</p>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 overflow-auto flex flex-col ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        } transition-all duration-300`}
      >
        <div className="sticky top-0 z-40">
          {/* Header */}
          <header className="bg-slate-800/50 border-b border-slate-700/50 px-6 py-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Menu size={20} />
                </button>
                <div className="hidden lg:block">
                  <GlobalSearch />
                </div>
              </div>
              <div className="flex items-center gap-4 min-w-0">
                {/* Notification Center */}
                {isAuthenticated && <NotificationCenter />}

                {/* User Menu */}
                {isAuthenticated ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors min-w-0"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: 'linear-gradient(to bottom right, var(--accent-400), var(--accent-500))' }}
                      >
                        <User size={16} />
                      </div>
                      <span className="text-sm font-medium truncate max-w-[120px] lg:max-w-none">{user?.full_name || 'User'}</span>
                      <ChevronDown size={16} className={`transition-transform shrink-0 ${showUserMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-lg py-1 z-50">
                        <div className="px-4 py-2 border-b border-slate-700">
                          <p className="text-sm font-medium">{user?.full_name}</p>
                          <p className="text-xs text-slate-400">{user?.email}</p>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                        >
                          <LogOut size={16} />
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/login')}
                    style={{ backgroundColor: 'var(--accent-500)', color: '#0f172a' }}
                    className="flex items-center gap-2 px-4 py-2 font-semibold rounded-lg transition-colors"
                  >
                    <LogIn size={16} />
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </header>

          {/* Announcement Banner */}
          <AnnouncementBanner />

          {/* Trial / past-due / cancelled banner (v28-3-2) */}
          <TrialBanner />

          {/* Live Price Ticker */}
          {isAuthenticated && showPriceTicker && (
            <div className="overflow-x-auto">
              <PriceTicker />
            </div>
          )}
        </div>

        {/* Page Content */}
        <div className="flex-1 pb-20 lg:pb-0">
          {entitlements?.investment_banking_gate && entitlements.investment_banking?.source === 'paid' && (
            <PaymentReminderCard reminder={entitlements.investment_banking.reminder_card as 'none' | 'due_soon' | 'overdue'} daysUntilDue={entitlements.investment_banking.days_until_due as number | null} />
          )}
          {children}
        </div>

        {/* FAIS Persistent Disclaimer Footer */}
        <DisclaimerFooter />
      </main>

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-[39]"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Onboarding Tour */}
      <OnboardingTour
        isOpen={showOnboarding}
        onComplete={() => setShowOnboarding(false)}
      />

      {/* Mobile Menu Overlay — shown when mobileMenuOpen=true, hidden at lg+ */}
      <MobileMenuOverlay isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Mobile Tab Bar — hidden at lg+ (desktop sidebar takes over) */}
      <MobileTabBar onMenuOpen={() => setMobileMenuOpen(true)} bankerLocked={bankerLocked} />
    </div>
  );
};

export default Layout;
